<template>
  <el-col :span="24" id="jes" v-loading.body="changingPage && !navigating" element-loading-text="Fetching page...">
    <ListViewHeader
      title="JOURNAL ENTRIES"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="'NewJe'">
    </ListViewHeader>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        :autoApply="false"
        clearAction="clearJeState"
        clearMetadataAction="resetJeMeta"
        searchAction="fetchJesWithQuery"
        transactionType="Je"
        :lineFieldSearch="otherNameFieldEnabled ? { field: 'other_name', transactionType: 'je' } : null"
        @filterCleared="() => $refs.filters.applyFilters()"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
          <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.type+'_je_ref'}`"/>
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfList v-if="jes" name="je_transactions_conf_list" ref="transactions_conf_list" :aggridColumns="aggridColumns"
          :rows="jes" :currentPage="currentPage"/>
        <el-row v-if="!meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import TransactionsConfList from '@/components/SigmaClientNew/TransactionsConfList.vue'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      ListViewHeader,
      TransactionsConfList
    },
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        statusChangeModalVisible: false,
        transactionToUpdate: null,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'dateColumn', 'descriptionColumn', 'currencyColumn', 'debitsAmountColumn', 'batchColumn', 'actionsColumn'],
        selectedStatuses: ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'CLOSED', 'VOIDED'],
        statusOptions: [
          { label: 'DRAFT', value: 'DRAFT' },
          { label: 'OPEN', value: 'OPEN' },
          { label: 'AUDITED', value: 'AUDITED' },
          { label: 'POSTED', value: 'POSTED' },
          { label: 'CLOSED', value: 'CLOSED' },
          { label: 'VOIDED', value: 'VOIDED' },
          { label: 'DELETED', value: 'DELETED' }
        ]
      }
    },
    methods: {
      getAppliedFilters () {
        this.$store.dispatch('clearSetJeFilterInfo')
        sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...this.$refs.filters.getFilters}))
        this.$store.dispatch('setJeFilterInfo', { page: this.currentPage, ...this.$refs.filters.getFilters })
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      /**
       * Handles paginating between JE pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          await this.$store.dispatch('setJeFilterInfo', { ...this.$refs.filters.getFilters, ...{ page: page } })
          // Get FilterInfo from the store data
          let filterStoreData = this.getJeFilterInfo
          if (Object.keys(filterStoreData).length) {
            this.filterRetained()
            return
          }
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page }, storeLines: false })
            : Object.assign({}, { query: { page }, storeLines: false })
          await this.$store.dispatch('clearJeState', [])
          await this.$store.dispatch('setJeFilterInfo', { page: page, ...this.currentFilters })
          const entities = await this.$store.dispatch('fetchJesWithQuery', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      async filterRetained () {
        this.changingPage = true
        // Get FilterInfo from the store data
        let filterStoreData = this.$store.getters.getJeFilterInfo
        if (!Object.keys(filterStoreData).length) {
          const query = { status__in: this.selectedStatuses }
          for (let f in this.currentFilters) {
            if (!query.hasOwnProperty(f)) {
              query[f] = this.currentFilters[f]
            }
          }
          const entities = await store.dispatch('fetchJesWithQuery', { query, storeLines: false })
          await store.dispatch('setFilter', query)
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...query}))
          await this.$store.dispatch('setJeFilterInfo', { ...query, ...{ page: this.currentPage } })
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          this.changingPage = false
          return
        } else {
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...filterStoreData}))
        }
        try {
          const { currentFilters } = this
          // Prepare payload query to append to the API
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage }, storeLines: false })
            : Object.assign({}, { query: { page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage }, storeLines: false })
          // Check 'keyword' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
            payload.query['search'] = filterStoreData['keyword']
          }
          // Check 'status__in' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
            payload.query['status__in'] = filterStoreData['status__in']
          }
          // Check 'batch' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch'] && filterStoreData['batch'].length > 0) {
            payload.query['batch'] = filterStoreData['batch']
          }
          // Check 'vendor' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['vendor'] && filterStoreData['vendor'].length > 0) {
            payload.query['vendor'] = filterStoreData['vendor']
          }
          // Check 'vendor' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
            payload.query['batch__bank__currency__in'] = filterStoreData['batch__bank__currency__in']
          }
          // Reset AP data
          await Promise.all([
            store.dispatch('resetJeMeta'),
            store.dispatch('clearJeState', true),
            store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
            store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
          ])
          // Get AP's data based on the filter query
          const entities = await store.dispatch('fetchJesWithQuery', payload)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          // Prefill the data for the filters
          if (this.filters && this.filters.length && this.$refs) {
            // Check 'keywordFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.keywordFilter_je_ref && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
              this.$refs.keywordFilter_je_ref[0].keyword = filterStoreData['keyword']
              this.$refs.keywordFilter_je_ref[0].change()
            }
            // Check 'selectFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.selectFilter_je_ref && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
              this.$refs.selectFilter_je_ref[0].selectedVal = filterStoreData['status__in']
              this.$refs.selectFilter_je_ref[0].change()
            }
             // Check 'selectFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.selectFilter_je_ref && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
              this.$refs.selectFilter_je_ref[1].selectedVal = filterStoreData['batch__bank__currency__in']
              this.$refs.selectFilter_je_ref[1].change()
            }
            // Check 'autocompleteFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.autocompleteFilter_je_ref && (filterStoreData['batch'] || filterStoreData['vendor'])) {
              // Check 'batch' data is exist or not, if exists then assing the value to prefill it
              if (filterStoreData && filterStoreData['batch']) {
                let batchInfo = this.getBatchById([filterStoreData['batch']])
                this.$refs.autocompleteFilter_je_ref[0].keyword = batchInfo.name
                this.$refs.autocompleteFilter_je_ref[0].handleSelect({label: batchInfo.name, value: batchInfo.id})
              }
            }
          }
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      }
    },
    computed: {
      ...mapGetters({
        jes: 'getJes',
        meta: 'getJeMetadata',
        currentPage: 'getCurrentJePage',
        getBatchById: 'getBatchById',
        selection: 'getSelectedTransactions',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        getcurrentFilters: 'getFilters',
        getJeFilterInfo: 'getJeFilterInfo'
      }),
      /**
       * Determines whether the 'other_name' line field is enabled in admin settings
       * @return {boolean}
       */
      otherNameFieldEnabled () {
        const fieldOrderSetting = JSON.parse(this.$store.getters.getUiSettings['LINE_FIELD_ORDER'])
        if (!(fieldOrderSetting && fieldOrderSetting.je && fieldOrderSetting.je.other_name)) {
          return false
        }
        return !fieldOrderSetting.je.other_name.hidden
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: this.statusOptions || [],
              defaults: this.selectedStatuses || [],
              multi: true,
              placeholder: 'Status'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'batch__bank__currency',
              clearable: true,
              opts: this.$store.getters.getAllCurrencies.map(c => ({ label: c.code, value: c.id })),
              multi: true,
              placeholder: 'Currency',
              styles: '0 4px'
            }
          }
        ]
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('jes').offsetHeight
          const headerHeight = document.getElementById('list-view-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    beforeDestroy () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    },
    async beforeRouteEnter (to, from, next) {
      try {
        await Promise.all([
          store.dispatch('resetJeMeta'),
          store.dispatch('clearJeState', true),
          store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ])
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-button--info.is-plain {
    background: #e1f5fe;
    border: 1px solid #bfcbd9;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Jes/Jes.vue